import React from "react";
var __jsx = React.createElement;
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Icon } from "@syarahtech/ui";
import { useLang } from "@/common/Contexts/LanguageContext";
import { useTranslation } from "@i18n";
import { checkFeatureFlag } from "@/common/config/websiteConfig";
import { twMerge } from "tailwind-merge";

// Mock components for demonstration
var Loader = function Loader() {
  return /*#__PURE__*/React.createElement("div", {
    className: "flex justify-center items-center p-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
  }));
};

// Mock SingleDay component
export var SingleDay = function SingleDay(_ref) {
  var activate = _ref.activate,
    day = _ref.day,
    dayName = _ref.dayName,
    isActive = _ref.isActive,
    disabled = _ref.disabled;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-2 items-center"
  }, /*#__PURE__*/React.createElement("span", {
    className: twMerge("text-sm font-semibold text-gray-text", isActive && "text-primary font-bold")
  }, dayName), /*#__PURE__*/React.createElement("div", {
    onClick: disabled ? undefined : activate,
    "data-enabled": !disabled,
    role: "button",
    className: twMerge("flex flex-col items-center justify-center px-4 py-3 rounded border-2 transition-all duration-200 outline-0 focus:outline-none border-solid cursor-pointer border-gray-gainsboro", isActive && "border-primary bg-[#E1EBFD] text-primary font-bold ", disabled && "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 cursor-not-allowed")
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-lg font-semibold ".concat(isActive ? 'text-blue-700' : disabled ? 'text-gray-400' : 'text-gray-800')
  }, day))));
};
var FULL_DAYS_LIMIT = 21;
var APPOINTMENT_RESCHEDULE_AUTO_SELECT_FIRST_DAY = checkFeatureFlag("FF_APPOINTMENT_RESCHEDULE_AUTO_SELECT_FIRST_DAY");
var DaysComponent = function DaysComponent(_ref2) {
  var myTimestamp = _ref2.myTimestamp,
    availableDays = _ref2.availableDays,
    selectDay = _ref2.selectDay;
  var _useState = useState({}),
    active = _useState[0],
    setActive = _useState[1];
  var scrollContainerRef = useRef(null);
  var dayRefs = useRef([]);
  var lang = useLang();
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;

  // Memoize days calculation
  var days = useMemo(function () {
    if (!myTimestamp || !(availableDays !== null && availableDays !== void 0 && availableDays.length)) return [];
    return myTimestamp === null || myTimestamp === void 0 ? void 0 : myTimestamp.nextNDays(FULL_DAYS_LIMIT, availableDays).days;
  }, [myTimestamp, availableDays]);

  // Memoize first available day
  var firstAvailableDay = useMemo(function () {
    return days.find(function (day) {
      return day === null || day === void 0 ? void 0 : day.available;
    });
  }, [days]);

  // Scroll function with better performance
  var scroll = useCallback(function (direction) {
    if (!scrollContainerRef.current) return;
    var scrollAmount = direction === "left" ? -120 : 120;
    scrollContainerRef.current.scrollBy({
      left: scrollAmount,
      behavior: "smooth"
    });
  }, []);

  // Activate day function
  var activateDay = useCallback(function (day, index) {
    if (!(day !== null && day !== void 0 && day.available)) return;
    var newActive = {
      idx: index + 1,
      timestamp: day.timestamp
    };
    setActive(newActive);
    selectDay(day.timestamp);
  }, [selectDay]);

  // Auto-select first available day on mount
  useEffect(function () {
    if (firstAvailableDay && !active.timestamp) {
      var index = days.indexOf(firstAvailableDay);
      activateDay(firstAvailableDay, index);
    }
  }, [firstAvailableDay, days, active.timestamp, activateDay]);

  // Feature flag auto-select
  useEffect(function () {
    if (APPOINTMENT_RESCHEDULE_AUTO_SELECT_FIRST_DAY && availableDays !== null && availableDays !== void 0 && availableDays.length) {
      var enabledElement = document.querySelector('[data-enabled="true"]');
      enabledElement === null || enabledElement === void 0 ? void 0 : enabledElement.click();
    }
  }, [availableDays === null || availableDays === void 0 ? void 0 : availableDays.length]);

  // Loading state
  if (!availableDays) {
    return /*#__PURE__*/React.createElement(Loader, null);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "relative w-full max-w-md mx-auto md:max-w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative flex items-center gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    onClick: function onClick() {
      return scroll("left");
    },
    className: "hidden absolute left-0 z-10 bg-white border border-solid border-gray-gainsboro transition-colors duration-200  md:relative rounded md:flex items-center justify-center min-w-6 min-h-6 mb-7 md:self-end cursor-pointer",
    "aria-label": "Previous days"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-forward-ios-rounded",
    width: 4,
    height: 4,
    className: "text-primary"
  })), /*#__PURE__*/React.createElement("div", {
    ref: scrollContainerRef,
    className: "flex gap-3 overflow-x-auto scrollbar-hide py-2",
    style: {
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }
  }, days.map(function (day, idx) {
    var _day$day;
    return /*#__PURE__*/React.createElement("div", {
      key: "".concat(day === null || day === void 0 ? void 0 : day.timestamp, "-").concat(idx),
      ref: function ref(el) {
        return dayRefs.current[idx] = el;
      },
      className: "flex-shrink-0"
    }, /*#__PURE__*/React.createElement(SingleDay, {
      activate: function activate() {
        return activateDay(day, idx);
      },
      day: day === null || day === void 0 ? void 0 : (_day$day = day.day) === null || _day$day === void 0 ? void 0 : _day$day.split("-")[2],
      dayName: lang === "ar" ? day === null || day === void 0 ? void 0 : day.dayNameAr : (day === null || day === void 0 ? void 0 : day.dayNameEn) || (day === null || day === void 0 ? void 0 : day.dayNameAr),
      isActive: active.idx === idx + 1,
      disabled: !(day !== null && day !== void 0 && day.available)
    }));
  })), /*#__PURE__*/React.createElement("div", {
    onClick: function onClick() {
      return scroll("right");
    },
    className: "hidden absolute left-0 z-10 bg-white border border-solid border-gray-gainsboro transition-colors duration-200  md:relative rounded md:flex items-center justify-center min-w-6 min-h-6 mb-7 md:self-end cursor-pointer",
    "aria-label": "Next days",
    role: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-forward-ios-rounded",
    width: 4,
    height: 4,
    className: "text-primary rotate-180"
  }))));
};
export var DaysComponentSkeleton = function DaysComponentSkeleton() {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "relative w-full max-w-md mx-auto md:max-w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative flex items-center gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex gap-3 overflow-x-auto scrollbar-hide py-2 flex-1"
  }, Array.from({
    length: 7
  }).map(function (_, idx) {
    return /*#__PURE__*/React.createElement("div", {
      key: idx,
      className: "flex-shrink-0"
    }, /*#__PURE__*/React.createElement(SingleDaySkeleton, null));
  })))));
};
export var SingleDaySkeleton = function SingleDaySkeleton() {
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-2 items-center"
  }, /*#__PURE__*/React.createElement("div", {
    className: "h-4 bg-gray-200 rounded w-12 animate-pulse"
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col items-center justify-center px-4 py-3 rounded border-2 border-gray-200 bg-gray-50 animate-pulse"
  }, /*#__PURE__*/React.createElement("div", {
    className: "h-6 bg-gray-200 rounded w-6"
  })));
};
export default DaysComponent;